import React from "react";
import { mlaAgreements } from "./data/mlaAgreements";

const MLA: React.FC = () => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "masih berlaku":
        return "badge-success";
      case "proses perpanjangan":
      case "proses perjanjian":
        return "badge-warning";
      case "habis masa berlaku":
      case "diberhentikan":
        return "badge-error";
      case "siap ditandatangani":
        return "badge-info";
      default:
        return "badge-neutral";
    }
  };

  return (
    <div>
      <div className="mx-auto w-11/12 laptop:max-w-full desktop:max-w-7xl">
        <div className="flex flex-row my-12">
          <div className="p-4 w-full">
            <div className="card w-full bg-base-100 shadow-xl border-t-8 border-blue-600 rounded-xl">
              <div className="card-body p-0">
                <div className="flex flex-row px-6 pt-6">
                  <div className="grow">
                    <h2 className="card-title text-3xl font-bold text-base-content">
                      MLA (Mutual Legal Assistance)
                    </h2>
                    <p className="text-base-content/70 mt-2">
                      Perjanjian Bantuan Hukum Timbal Balik dalam Kerja Sama
                      Internasional
                    </p>
                  </div>
                  <div className="shrink">
                    <div className="text-sm breadcrumbs">
                      <ul>
                        <li>
                          <a
                            href="/"
                            className="text-base-content/60 hover:text-base-content"
                          >
                            Beranda
                          </a>
                        </li>
                        <li className="text-base-content/60">
                          Kerja Sama Internasional
                        </li>
                        <li className="text-primary font-semibold">MLA</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="divider m-0"></div>

                <div className="p-6">
                  {/* Introduction */}
                  <div className="bg-base-200 rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Tentang MLA
                    </h3>
                    <p className="text-base-content/70 leading-relaxed">
                      Mutual Legal Assistance (MLA) atau Bantuan Hukum Timbal
                      Balik merupakan mekanisme kerja sama internasional untuk
                      saling memberikan bantuan dalam penanganan perkara pidana
                      lintas negara. Polri telah menjalin berbagai perjanjian
                      MLA dengan negara-negara di seluruh dunia untuk memperkuat
                      kerja sama dalam pencegahan dan pemberantasan kejahatan
                      transnasional.
                    </p>
                  </div>

                  {/* Agreements by Continent */}
                  <div className="space-y-8">
                    {mlaAgreements.map((continent) => (
                      <div
                        key={continent.name}
                        className="bg-base-200 rounded-xl p-6"
                      >
                        <h3 className="text-2xl font-bold text-primary mb-6 border-b border-base-300 pb-3">
                          {continent.name}
                        </h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {continent.countries.map((agreement, index) => (
                            <div
                              key={index}
                              className="bg-base-100 rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <h4 className="text-lg font-bold text-base-content">
                                  {agreement.country}
                                </h4>
                                <span
                                  className={`badge ${getStatusColor(agreement.status)} badge-sm`}
                                >
                                  {agreement.status}
                                </span>
                              </div>

                              <div className="space-y-3">
                                <div>
                                  <p className="text-sm font-semibold text-base-content/80 mb-2">
                                    Judul Perjanjian:
                                  </p>
                                  <p className="text-sm text-base-content/70 leading-relaxed">
                                    {agreement.title}
                                  </p>
                                </div>

                                {agreement.organizations && (
                                  <div>
                                    <p className="text-sm font-semibold text-base-content/80 mb-2">
                                      Detail Organisasi:
                                    </p>
                                    <ul className="space-y-1">
                                      {agreement.organizations.map(
                                        (org, orgIndex) => (
                                          <li
                                            key={orgIndex}
                                            className="text-xs text-base-content/60 leading-relaxed"
                                          >
                                            • {org}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Statistics */}
                  <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Ringkasan Statistik
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-primary">5</div>
                        <div className="text-sm text-base-content/70">
                          Benua
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-secondary">
                          30+
                        </div>
                        <div className="text-sm text-base-content/70">
                          Negara
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-success">
                          25
                        </div>
                        <div className="text-sm text-base-content/70">
                          Masih Berlaku
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-warning">4</div>
                        <div className="text-sm text-base-content/70">
                          Proses
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-accent">
                          50+
                        </div>
                        <div className="text-sm text-base-content/70">
                          Total Perjanjian
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MLA;
